import React, { useEffect, ReactNode } from 'react'

import { Item, Section, useListState } from 'react-stately';
import { mergeProps, useFocusRing, useListBox, useOption, useListBoxSection } from 'react-aria';

import clsx from 'clsx';

import styleClasses from "./ListBox.module.css";

interface ListBoxItem {
  label: string;
  value: string | number;
  isDisabled?: boolean;
  items?: ListBoxItem[];
}

interface ListBoxProps {
  items: ListBoxItem[];
  isMulti?: boolean;
  onSelect: (selection: any) => void;
  selected?: string | string[];
  [key: string]: any;
}

export default function ListBox(props: ListBoxProps) {

    const {
        items, ...otherProps
    } = props;

    function collectDisabledKeys(items: ListBoxItem[]): (string | number)[] {
        return items.flatMap(item => {
            const { label, value, isDisabled, items } = item
            return items ? collectDisabledKeys(items) : (isDisabled ? [value] : [])
        })
    }
    const disabledKeys = collectDisabledKeys(items);

    function renderItem(item: ListBoxItem, index: number) {
        const { label, value, items } = item
        if (items) {
            // section
            return (
                <Section title={item.label} key={index} aria-label={item.label}>
                    {items.map(renderItem)}
                </Section>
            )
        } else {

            return (
                <Item key={value}>
                    {label}
                </Item>
            )
        }
    }

    return (
        <ListBox_comp {...otherProps} disabledKeys={disabledKeys}>
            {items.map(renderItem)}
        </ListBox_comp>
    )
}

interface ListBoxCompProps {
  isMulti?: boolean;
  onSelect: (selection: any) => void;
  selected?: string | string[] | null;
  children: ReactNode;
  disabledKeys?: (string | number)[];
  size?: 'small' | 'normal' | 'large';
  [key: string]: any;
}

function ListBox_comp(props: ListBoxCompProps) {
    // Create state based on the incoming props
    const {
        isMulti, onSelect, selected, size,
        ...otherProps
    } = props;

    // @ts-ignore - 我们需要忽略类型检查，因为react-stately的类型定义有限制
    const state = useListState({
        ...otherProps,
        onSelectionChange: selection => {
            onSelect(isMulti ? Array.from(selection) : selection);
        },
        selectedKeys: selected ? (Array.isArray(selected) ? new Set(selected) : new Set([selected])) : undefined,
        disallowEmptySelection: false
    });

    useEffect(() => {
        state.selectionManager.setSelectionBehavior('replace');
        // @ts-ignore - allowsMultipleSelection exists but is not in the type definitions
        state.selectionManager.allowsMultipleSelection = isMulti;

        const currentKeys = Array.from(state.selectionManager.selectedKeys);
        const newKeys = Array.isArray(selected) ? selected : (selected ? [selected] : []);
        
        // 只有当选中的键真正发生变化时才更新
        if (JSON.stringify(currentKeys.sort()) !== JSON.stringify(newKeys.sort())) {
            state.selectionManager.setSelectedKeys(new Set(newKeys));
        }
    }, [selected, isMulti]);
    
    return (
        <ListBoxStateless {...props} state={state} size={size} />
    )
}

interface ListBoxStatelessProps {
  color?: string;
  style?: React.CSSProperties;
  optionStyle?: React.CSSProperties;
  sectionStyle?: React.CSSProperties;
  state: any;
  isMulti?: boolean;
  itemUnstyled?: boolean;
  className?: string;
  label?: ReactNode;
  selected?: string | string[] | null;
  size?: 'small' | 'normal' | 'large';
  'aria-label'?: string;
  'aria-labelledby'?: string;
  [key: string]: any;
}

export function ListBoxStateless(props: ListBoxStatelessProps) {
    const {
        color = "main", 
        style, 
        optionStyle, 
        sectionStyle, 
        state,
        isMulti,
        itemUnstyled = false,
        className,
        label,
        selected,
        size = 'normal',
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledby,
        ...otherProps
    } = props;

    // Get props for the listbox element
    const ref = React.useRef(null);
    const { listBoxProps, labelProps } = useListBox({
        ...otherProps,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledby,
        label,
        selectionMode: isMulti ? 'multiple' : 'single'
    }, state, ref);

    return (
        <ul
            {...listBoxProps}
            className={clsx(
                styleClasses["lc-list-box"], 
                "lc-base",
                className,
                {
                    "font-size-13 py-1 px-2": size === 'small',
                    "font-size-14 py-1 px-2": size === 'normal',
                    "font-size-16 py-1.5 px-3": size === 'large'
                }
            )}
            ref={ref}
            data-color={color}
            data-selection-mode={isMulti ? 'multiple' : 'single'}
            data-size={size}
            style={{
                ...(style || {}),
            }}
        >
            {[...state.collection].map((item: any) => (
                item.type === 'section'
                    ? <ListBoxSection 
                        key={item.key} 
                        section={item} 
                        state={state} 
                        style={sectionStyle} 
                        itemUnstyled={itemUnstyled}
                        selected={selected}
                        size={size}
                      />
                    : <Option 
                        key={item.key} 
                        item={item} 
                        state={state} 
                        selected={selected}
                        style={optionStyle} 
                        aria-label={item.key} 
                        itemUnstyled={itemUnstyled}
                        size={size}
                      />
            ))}
        </ul>
    );
}


ListBox.Item = Item;
ListBox.Section = Section;

interface OptionProps {
  item: any;
  state: any;
  style?: React.CSSProperties;
  itemUnstyled?: boolean;
  selected?: string | string[] | null;
  'aria-label'?: string;
  size?: 'small' | 'normal' | 'large';
}

function Option({ item, state, style, itemUnstyled, selected, size }: OptionProps) {
    const ref = React.useRef(null);
    const {
        optionProps,
        labelProps,
        descriptionProps,        
        // isSelected,
        isDisabled,
        isFocused,
        isFocusVisible
    } = useOption(
        { key: item.key },
        state,
        ref
    );

    const isSelected = Array.isArray(selected) ? selected.includes(item.key) : selected === item.key;

    return (
        <li 
            {...optionProps}
            ref={ref}
            className={clsx(
                itemUnstyled ? styleClasses["list-box-item-unstyled"] : styleClasses["list-box-item"],
                {
                    "py-1 px-2": size === 'small',
                    "py-1.5 px-2": size === 'normal',
                    "py-2 px-3": size === 'large'
                }
            )} 
            data-focused={isFocused}
            data-selected={isSelected}
            data-disabled={isDisabled}
            data-keyboard-focus={isFocusVisible}
            data-size={size}
            style={style}
        >
            {item.rendered}
        </li>
    );
}

interface ListBoxSectionProps {
  section: any;
  state: any;
  style?: React.CSSProperties;
  itemUnstyled?: boolean;
  selected?: string | string[] | null;
  size?: 'small' | 'normal' | 'large';
}

function ListBoxSection({ section, state, style, itemUnstyled, selected, size }: ListBoxSectionProps) {
    let { itemProps, headingProps, groupProps } = useListBoxSection({
        heading: section.rendered,
        'aria-label': section['aria-label']
    });

    // If the section is not the first, add a separator element to provide visual separation.
    // The heading is rendered inside an <li> element, which contains
    // a <ul> with the child items.
    return (
        <>
            {section.key !== state.collection.getFirstKey() &&
                <li role="presentation" className={styleClasses["section-seperator"]} />
            }
            <li {...itemProps} className={styleClasses["list-box-section"]}>
                {section.rendered &&
                    (
                        <span
                            {...headingProps}
                            className={styleClasses["section-head"]}
                        >
                            {section.rendered}
                        </span>
                    )}
                <ul
                    {...groupProps}
                    style={style}
                >
                    {[...section.childNodes].map((node: any) => (
                        <Option
                            key={node.key}                    
                            item={node}
                            state={state}
                            style={style}
                            itemUnstyled={itemUnstyled}
                            selected={selected}
                            size={size}
                        />
                    ))}
                </ul>
            </li>
        </>
    );
}